// src/dashboard/settings/AgentAssignmentSettings.jsx

import React, { useEffect, useContext, useReducer, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { db } from '../../config/firebaseConfig';
import { collection, getDocs, query, addDoc, deleteDoc, doc, getDoc, setDoc } from 'firebase/firestore';
import { AuthContext } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { GitBranch, Trash2, PlusCircle } from 'lucide-react';

const initialState = {
  agents: [],
  assignments: [],
  locations: {},
  loading: true,
  isSaving: false,
  error: null,
  assignmentStrategy: 'round_robin',
  sortKey: 'agent_name',
  sortDirection: 'asc',
  filterAgent: '',
  filterCity: '',
  filterNeighborhood: '',
  form: {
    selectedAgent: '',
    city: '',
    neighborhoodInput: '',
    neighborhoods: [],
  },
};

function agentAssignmentReducer(state, action) {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, loading: true, error: null };
    case 'FETCH_SUCCESS':
      return { ...state, loading: false, ...action.payload };
    case 'FETCH_ERROR':
      return { ...state, loading: false, error: action.payload };
    case 'SET_STRATEGY':
      return { ...state, assignmentStrategy: action.payload };
    case 'SET_SORTING': {
      const isSame = state.sortKey === action.payload;
      const dir = isSame && state.sortDirection === 'asc' ? 'desc' : 'asc';
      return { ...state, sortKey: action.payload, sortDirection: dir };
    }
    case 'SET_FILTER':
      return { ...state, [action.field]: action.payload };
    case 'SET_FORM_FIELD': {
      if (action.field === 'city') {
        return {
          ...state,
          form: { ...state.form, city: action.payload, neighborhoods: [], neighborhoodInput: '' },
        };
      }
      return { ...state, form: { ...state.form, [action.field]: action.payload } };
    }
    case 'SET_NEIGHBORHOOD_INPUT':
      return { ...state, form: { ...state.form, neighborhoodInput: action.payload } };
    case 'ADD_NEIGHBORHOOD': {
      const n = action.payload.trim().toLowerCase();
      if (n && !state.form.neighborhoods.includes(n)) {
        return {
          ...state,
          form: {
            ...state.form,
            neighborhoods: [...state.form.neighborhoods, n],
            neighborhoodInput: '',
          },
        };
      }
      return state;
    }
    case 'REMOVE_NEIGHBORHOOD':
      return {
        ...state,
        form: { ...state.form, neighborhoods: state.form.neighborhoods.filter((x) => x !== action.payload) },
      };
    case 'ADD_RULE_START':
      return { ...state, isSaving: true };
    case 'ADD_RULE_SUCCESS':
      return {
        ...state,
        isSaving: false,
        assignments: [...state.assignments, action.payload],
        form: { ...initialState.form, selectedAgent: state.form.selectedAgent, city: state.form.city },
      };
    case 'ADD_RULE_ERROR':
      return { ...state, isSaving: false };
    case 'DELETE_RULE_SUCCESS':
      return { ...state, assignments: state.assignments.filter((a) => a.id !== action.payload) };
    default:
      return state;
  }
}

export default function AgentAssignmentSettings() {
  const intl = useIntl();
  const { user } = useContext(AuthContext);
  const [state, dispatch] = useReducer(agentAssignmentReducer, initialState);

  const {
    agents,
    assignments,
    locations,
    loading,
    isSaving,
    assignmentStrategy,
    form,
    filterAgent,
    filterCity,
    filterNeighborhood,
    sortKey,
    sortDirection,
  } = state;

  const { selectedAgent, city, neighborhoodInput, neighborhoods } = form;

  // Carregar contatos do WhatsApp, regras existentes e cache de cidades/bairros
  useEffect(() => {
    const fetchData = async () => {
      if (!user?.uid) return;
      dispatch({ type: 'FETCH_START' });
      try {
        // 1) Contatos configurados no Chatbot (usados como agentes)
        const chatbotSettingsRef = doc(db, 'users', user.uid, 'settings', 'chatbot');
        const settingsSnap = await getDoc(chatbotSettingsRef);
        const fetchedAgents = settingsSnap.exists() ? settingsSnap.data().whatsapp_contacts || [] : [];

        // 2) Regras de atribuição
        const assignmentsRef = collection(db, 'users', user.uid, 'agent_assignments');
        const assignmentsSnapshot = await getDocs(query(assignmentsRef));
        const fetchedAssignments = assignmentsSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

        // 3) Lista de cidades/bairros (gera se não existir)
        const locationsRef = doc(db, 'users', user.uid, 'settings', 'locations');
        const locationsSnap = await getDoc(locationsRef);
        let fetchedLocations = locationsSnap.exists() ? locationsSnap.data() : {};

        if (!locationsSnap.exists()) {
          toast.info(
            intl.formatMessage({
              id: 'agentAssignment.generatingLocations',
              defaultMessage: 'Analisando imóveis para gerar lista de cidades e bairros...',
            })
          );
          const propertiesRef = collection(db, 'users', user.uid, 'properties');
          const propertiesSnapshot = await getDocs(propertiesRef);
          const generated = {};
          propertiesSnapshot.forEach((p) => {
            const d = p.data();
            const c = d.cidade?.trim().toLowerCase();
            const b = d.bairro?.trim().toLowerCase();
            if (c && b) {
              if (!generated[c]) generated[c] = new Set();
              generated[c].add(b);
            }
          });
          Object.keys(generated).forEach((k) => {
            generated[k] = Array.from(generated[k]).sort();
          });
          fetchedLocations = generated;
          await setDoc(locationsRef, fetchedLocations);
          toast.success(
            intl.formatMessage({
              id: 'agentAssignment.locationsGeneratedSuccess',
              defaultMessage: 'Lista de locais gerada e salva com sucesso!',
            })
          );
        }

        dispatch({
          type: 'FETCH_SUCCESS',
          payload: { agents: fetchedAgents, assignments: fetchedAssignments, locations: fetchedLocations },
        });
      } catch (error) {
        console.error('Error fetching assignment data:', error);
        const msg = intl.formatMessage({ id: 'general.errorLoading', defaultMessage: 'Erro ao carregar dados.' });
        dispatch({ type: 'FETCH_ERROR', payload: msg });
        toast.error(msg);
      }
    };
    fetchData();
  }, [user, intl]);

  const handleNeighborhoodKeyDown = (e) => {
    if (e.key === 'Enter' && neighborhoodInput.trim()) {
      e.preventDefault();
      dispatch({ type: 'ADD_NEIGHBORHOOD', payload: neighborhoodInput.trim() });
    }
  };

  const handleAddAssignment = async (e) => {
    e.preventDefault();
    if (!selectedAgent || !city.trim() || neighborhoods.length === 0) {
      toast.warn(
        intl.formatMessage({
          id: 'agentAssignment.form.validationError',
          defaultMessage: 'Selecione corretor, cidade e pelo menos um bairro.',
        })
      );
      return;
    }
    const normalizedCity = city.trim().toLowerCase();
    const isDuplicate = assignments.some(
      (a) =>
        a.agent_id === selectedAgent &&
        a.cidade === normalizedCity &&
        a.bairros.some((b) => neighborhoods.includes(b))
    );
    if (isDuplicate) {
      toast.warn(
        intl.formatMessage({
          id: 'agentAssignment.duplicateRuleWarning',
          defaultMessage: 'Essa combinação de corretor e região já existe.',
        })
      );
      return;
    }

    dispatch({ type: 'ADD_RULE_START' });

    const agentInfo = agents.find((a) => a.id === selectedAgent);
    const newAssignment = {
      agent_id: selectedAgent,
      agent_name: agentInfo?.name || 'N/A',
      whatsapp: agentInfo?.number || '',
      cidade: normalizedCity,
      bairros: neighborhoods,
    };

    try {
      const assignmentsRef = collection(db, 'users', user.uid, 'agent_assignments');
      const docRef = await addDoc(assignmentsRef, newAssignment);
      dispatch({ type: 'ADD_RULE_SUCCESS', payload: { id: docRef.id, ...newAssignment } });
      toast.success(intl.formatMessage({ id: 'agentAssignment.ruleAdded', defaultMessage: 'Regra adicionada.' }));
    } catch (error) {
      console.error('Error adding assignment rule:', error);
      dispatch({ type: 'ADD_RULE_ERROR' });
      toast.error(intl.formatMessage({ id: 'form.error.generic', defaultMessage: 'Erro ao salvar.' }));
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    if (!window.confirm(intl.formatMessage({ id: 'agentAssignment.confirmDelete', defaultMessage: 'Remover esta regra?' })))
      return;
    try {
      const assignmentDocRef = doc(db, 'users', user.uid, 'agent_assignments', assignmentId);
      await deleteDoc(assignmentDocRef);
      dispatch({ type: 'DELETE_RULE_SUCCESS', payload: assignmentId });
      toast.success(intl.formatMessage({ id: 'agentAssignment.ruleDeleted', defaultMessage: 'Regra removida.' }));
    } catch (error) {
      console.error('Error deleting assignment rule:', error);
      toast.error(intl.formatMessage({ id: 'general.deleteError', defaultMessage: 'Erro ao remover.' }));
    }
  };

  const sortedAndFilteredAssignments = useMemo(() => {
    const filtered = assignments
      .filter((a) => filterAgent === '' || a.agent_id === filterAgent)
      .filter((a) => filterCity === '' || a.cidade === filterCity)
      .filter(
        (a) =>
          filterNeighborhood.trim() === '' ||
          a.bairros.some((b) => b.toLowerCase().includes(filterNeighborhood.trim().toLowerCase()))
      );

    return filtered.sort((a, b) => {
      const valA = a[sortKey] || '';
      const valB = b[sortKey] || '';
      const comparison = valA.toString().localeCompare(valB.toString(), intl.locale, { sensitivity: 'base' });
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [assignments, filterAgent, filterCity, filterNeighborhood, sortKey, sortDirection, intl.locale]);

  if (loading) {
    return (
      <div className="la dashboard-page">
        <div className="dashboard-content">
          <p className="muted-text">{intl.formatMessage({ id: 'loading.label', defaultMessage: 'Carregando...' })}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="la dashboard-page">
      <div className="dashboard-content">
        {/* Header */}
        <div className="dashboard-header">
          <div className="header-left">
            <h2 className="page-title">
              {intl.formatMessage({ id: 'agentAssignment.title', defaultMessage: 'Atribuição de Agentes' })}
            </h2>
            <div className="page-subtitle">
              {intl.formatMessage({
                id: 'agentAssignment.subtitle',
                defaultMessage: 'Defina regras por cidade e bairro para direcionar leads ao corretor certo.',
              })}
            </div>
          </div>
        </div>

        {/* Estratégia */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">
              {intl.formatMessage({ id: 'agentAssignment.strategyTitle', defaultMessage: 'Estratégia de atribuição' })}
            </h3>
          </div>
          <select
            className="input-field mt-16"
            value={assignmentStrategy}
            onChange={(e) => dispatch({ type: 'SET_STRATEGY', payload: e.target.value })}
          >
            <option value="round_robin">
              {intl.formatMessage({ id: 'agentAssignment.strategy.roundRobin', defaultMessage: 'Round-robin' })}
            </option>
            <option value="first_available" disabled>
              {intl.formatMessage({
                id: 'agentAssignment.strategy.firstAvailable',
                defaultMessage: 'Primeiro disponível',
              })}{' '}
              (em breve)
            </option>
          </select>
        </div>

        {/* Nova Regra */}
        <div className="card mt-24">
          <div className="card-header">
            <h3 className="card-title">
              {intl.formatMessage({ id: 'agentAssignment.rulesTitle', defaultMessage: 'Regras de atribuição' })}
            </h3>
            <p className="card-subtitle">
              {intl.formatMessage({
                id: 'agentAssignment.rulesSubtitle',
                defaultMessage:
                  'Selecione o corretor, a cidade e adicione um ou mais bairros para criar uma nova regra.',
              })}
            </p>
          </div>

          <form onSubmit={handleAddAssignment} className="form-grid">
            <div className="form-group">
              <label>{intl.formatMessage({ id: 'agentAssignment.agent', defaultMessage: 'Corretor' })}</label>
              <select
                className="input-field"
                value={selectedAgent}
                onChange={(e) => dispatch({ type: 'SET_FORM_FIELD', field: 'selectedAgent', payload: e.target.value })}
              >
                <option value="">
                  {intl.formatMessage({ id: 'general.select', defaultMessage: 'Selecione...' })}
                </option>
                {agents.map((agent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.name} ({agent.number})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>{intl.formatMessage({ id: 'agentAssignment.city', defaultMessage: 'Cidade' })}</label>
              <select
                className="input-field"
                value={city}
                onChange={(e) => dispatch({ type: 'SET_FORM_FIELD', field: 'city', payload: e.target.value })}
                disabled={Object.keys(locations).length === 0}
              >
                <option value="">
                  {Object.keys(locations).length > 0
                    ? intl.formatMessage({ id: 'agentAssignment.selectCity', defaultMessage: 'Selecione a cidade' })
                    : intl.formatMessage({ id: 'agentAssignment.noCitiesFound', defaultMessage: 'Nenhuma cidade encontrada' })}
                </option>
                {Object.keys(locations)
                  .sort()
                  .map((c) => (
                    <option key={c} value={c} className="text-capitalize">
                      {c}
                    </option>
                  ))}
              </select>
            </div>

            <div className="form-group full-width">
              <label>{intl.formatMessage({ id: 'agentAssignment.neighborhood', defaultMessage: 'Bairros' })}</label>
              <div className="tag-input-container">
                {neighborhoods.map((n) => (
                  <div key={n} className="tag-chip">
                    {n}
                    <button
                      type="button"
                      aria-label="Remover bairro"
                      onClick={() => dispatch({ type: 'REMOVE_NEIGHBORHOOD', payload: n })}
                    >
                      &times;
                    </button>
                  </div>
                ))}
                <input
                  type="text"
                  className="tag-input-field"
                  value={neighborhoodInput}
                  onChange={(e) => dispatch({ type: 'SET_NEIGHBORHOOD_INPUT', payload: e.target.value })}
                  onKeyDown={handleNeighborhoodKeyDown}
                  placeholder={intl.formatMessage({
                    id: 'agentAssignment.addNeighborhoodPlaceholder',
                    defaultMessage: 'Digite um bairro e pressione Enter',
                  })}
                  disabled={!city}
                  list="neighborhood-suggestions"
                />
                <datalist id="neighborhood-suggestions">
                  {city &&
                    locations[city] &&
                    locations[city].map((b) => (
                      <option key={b} value={b} />
                    ))}
                </datalist>
              </div>
            </div>

            <div className="form-group full-width text-right">
              <button type="submit" className="primary-button add-rule-btn" disabled={isSaving}>
                <span className="row center gap-8">
                  <PlusCircle size={16} />
                  {isSaving
                    ? intl.formatMessage({ id: 'general.saving', defaultMessage: 'Salvando…' })
                    : intl.formatMessage({ id: 'agentAssignment.addRule', defaultMessage: 'Adicionar regra' })}
                </span>
              </button>
            </div>
          </form>

          {/* Lista de Regras */}
          <div className="table-container">
            <div className="filters-container">
              <div className="form-group" style={{ flex: 1, minWidth: '200px', marginBottom: 0 }}>
                <label>
                  {intl.formatMessage({ id: 'agentAssignment.filter.byAgent', defaultMessage: 'Filtrar por corretor' })}
                </label>
                <select
                  className="input-field"
                  value={filterAgent}
                  onChange={(e) => dispatch({ type: 'SET_FILTER', field: 'filterAgent', payload: e.target.value })}
                >
                  <option value="">
                    {intl.formatMessage({ id: 'agentAssignment.filter.allAgents', defaultMessage: 'Todos os corretores' })}
                  </option>
                  {agents.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group" style={{ flex: 1, minWidth: '200px', marginBottom: 0 }}>
                <label>
                  {intl.formatMessage({ id: 'agentAssignment.filter.byCity', defaultMessage: 'Filtrar por cidade' })}
                </label>
                <select
                  className="input-field"
                  value={filterCity}
                  onChange={(e) => dispatch({ type: 'SET_FILTER', field: 'filterCity', payload: e.target.value })}
                  disabled={Object.keys(locations).length === 0}
                >
                  <option value="">
                    {intl.formatMessage({ id: 'agentAssignment.filter.allCities', defaultMessage: 'Todas as cidades' })}
                  </option>
                  {Object.keys(locations)
                    .sort()
                    .map((c) => (
                      <option key={c} value={c} className="text-capitalize">
                        {c}
                      </option>
                    ))}
                </select>
              </div>

              <div className="form-group" style={{ flex: 1, minWidth: '200px', marginBottom: 0 }}>
                <label>
                  {intl.formatMessage({
                    id: 'agentAssignment.filter.byNeighborhood',
                    defaultMessage: 'Filtrar por bairro (contém)',
                  })}
                </label>
                <input
                  type="text"
                  className="input-field"
                  placeholder={intl.formatMessage({
                    id: 'agentAssignment.filter.neighborhoodPlaceholder',
                    defaultMessage: 'Digite parte do nome do bairro',
                  })}
                  value={filterNeighborhood}
                  onChange={(e) =>
                    dispatch({ type: 'SET_FILTER', field: 'filterNeighborhood', payload: e.target.value })
                  }
                />
              </div>
            </div>

            <table>
              <thead>
                <tr>
                  <th className="sortable-header" onClick={() => dispatch({ type: 'SET_SORTING', payload: 'agent_name' })}>
                    {intl.formatMessage({ id: 'agentAssignment.agent', defaultMessage: 'Corretor' })}
                    {sortKey === 'agent_name' ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : ''}
                  </th>
                  <th className="sortable-header" onClick={() => dispatch({ type: 'SET_SORTING', payload: 'cidade' })}>
                    {intl.formatMessage({ id: 'agentAssignment.city', defaultMessage: 'Cidade' })}
                    {sortKey === 'cidade' ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : ''}
                  </th>
                  <th>{intl.formatMessage({ id: 'agentAssignment.neighborhood', defaultMessage: 'Bairros' })}</th>
                  <th>{intl.formatMessage({ id: 'agentAssignment.actions', defaultMessage: 'Ações' })}</th>
                </tr>
              </thead>
              <tbody>
                {sortedAndFilteredAssignments.length > 0 ? (
                  sortedAndFilteredAssignments.map((assign) => (
                    <tr key={assign.id}>
                      <td>{assign.agent_name}</td>
                      <td className="text-capitalize">{assign.cidade}</td>
                      <td>
                        {assign.bairros?.map((b) => (
                          <span key={b} className="badge-chip">
                            {b}
                          </span>
                        ))}
                      </td>
                      <td>
                        <button
                          onClick={() => handleDeleteAssignment(assign.id)}
                          className="danger-button btn-xs"
                          aria-label={intl.formatMessage({
                            id: 'general.delete',
                            defaultMessage: 'Excluir',
                          })}
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4">
                      {intl.formatMessage({ id: 'agentAssignment.noRules', defaultMessage: 'Nenhuma regra cadastrada.' })}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}


